import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ChevronRightIcon } from 'lucide-react'
import { EmptyState } from '@/components/empty-state'
import { Favicon } from '@/components/favicon'
import { Panel } from '@/components/panel'
import { type QueueItem, useOutreachConfig, useOutreachQueue } from '@/lib/outreach'
import { SectionHeader, TableSkeleton, useSiteScope } from '../../-section'
import { pathOf, preview, TAG } from '../-draft'
import { SourceChip } from '../-parts'
import {
  dayKey,
  dayLabel,
  dayStart,
  PLAN_DAYS,
  type Plan,
  ScheduleControl,
  upcomingDays,
  usePlan,
} from '../-schedule'

export const Route = createFileRoute('/_app/$org/$host/outreach/_list/approved')({
  component: ApprovedPage,
})

/**
 * The week's plan. Approved mail goes nowhere until it is given a day, so the
 * page is what is still waiting for one and then each day with what it holds
 * against the cap.
 */
function ApprovedPage() {
  const { org, host, site } = useSiteScope()
  const { data, isPending } = useOutreachQueue(site, 'approved')
  const { data: config } = useOutreachConfig(site)
  const plan = usePlan(site)

  const state = config?.config
  const held = state ? (!state.enabled ? 'off' : state.dryRun ? 'dry run' : null) : null
  const header = (
    <SectionHeader
      title="Approved"
      actions={
        held && (
          <Link to="/$org/$host/outreach/settings" params={{ org, host }} className={TAG}>
            {held}
          </Link>
        )
      }
    />
  )

  if (isPending && !data) {
    return (
      <>
        {header}
        <TableSkeleton rows={3} />
      </>
    )
  }

  const items = data?.items ?? []
  if (items.length === 0) {
    return (
      <>
        {header}
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            title="Nothing approved"
            description={`No mail is waiting to leave ${host}.`}
          />
        </div>
      </>
    )
  }

  const today = dayKey(new Date())

  const unscheduled: QueueItem[] = []
  const byDay = new Map<string, QueueItem[]>()
  for (const item of items) {
    if (!item.message.scheduledFor) {
      unscheduled.push(item)
      continue
    }
    const key = dayKey(new Date(item.message.scheduledFor))
    byDay.set(key, [...(byDay.get(key) ?? []), item])
  }

  const days = [...new Set([...upcomingDays(PLAN_DAYS), ...byDay.keys()])].sort()

  return (
    <>
      {header}
      <div className="flex flex-col gap-4">
        {unscheduled.length > 0 && (
          <Panel flush>
            <PlanGroup
              label="No day"
              items={unscheduled}
              org={org}
              host={host}
              siteId={site?.id}
              plan={plan}
            />
          </Panel>
        )}
        <Panel flush>
          {days.map((key) => (
            <PlanGroup
              key={key}
              label={dayLabel.format(dayStart(key))}
              count={key < today ? undefined : `${plan.load(key)}/${plan.cap}`}
              items={byDay.get(key) ?? []}
              org={org}
              host={host}
              siteId={site?.id}
              plan={plan}
              late={key < today}
            />
          ))}
        </Panel>
      </div>
    </>
  )
}

function PlanGroup({
  label,
  count,
  items,
  org,
  host,
  siteId,
  plan,
  late = false,
}: {
  label: string
  count?: string
  items: QueueItem[]
  org: string
  host: string
  siteId: string | undefined
  plan: Plan
  late?: boolean
}) {
  return (
    <section className="border-b border-border last:border-b-0">
      <header className="flex items-center gap-2 bg-secondary/30 px-4 py-2">
        <span className="mono-label text-muted-foreground">{label}</span>
        {late && <span className={TAG}>late</span>}
        {count && (
          <span className="ml-auto font-mono text-[11px] text-muted-foreground tabular">
            {count}
          </span>
        )}
      </header>
      {items.length > 0 && (
        <table className="w-full border-collapse text-[13px]">
          <tbody>
            {items.map((item) => (
              <PlanRow
                key={item.message.id}
                item={item}
                org={org}
                host={host}
                siteId={siteId}
                plan={plan}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function PlanRow({
  item: { message, prospect },
  org,
  host,
  siteId,
  plan,
}: {
  item: QueueItem
  org: string
  host: string
  siteId: string | undefined
  plan: Plan
}) {
  const navigate = useNavigate()
  const open = () =>
    navigate({
      to: '/$org/$host/outreach/$prospectId',
      params: { org, host, prospectId: prospect.id },
    })

  return (
    <tr
      className="group cursor-pointer border-t border-border hover:bg-secondary/40"
      onClick={open}
    >
      <td className="w-[96px] py-2.5 pr-3 pl-4 align-top">
        <ScheduleControl
          messageId={message.id}
          scheduledFor={message.scheduledFor}
          siteId={siteId}
          plan={plan}
        />
      </td>
      <td className="w-[24%] max-w-0 py-2.5 pr-3 align-top">
        <div className="flex min-w-0 items-center gap-2">
          <Favicon host={prospect.domain} size={14} className="shrink-0 rounded-[3px]" />
          <span className="min-w-0 truncate font-mono text-[12.5px] leading-5">
            {prospect.domain}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 pl-[22px]">
          <SourceChip source={prospect.source} />
          {message.sequence > 0 && <span className={TAG}>follow-up</span>}
        </div>
      </td>
      <td className="max-w-0 py-2.5 pr-3 align-top">
        <p className="truncate text-[13px] leading-5 font-medium" title={message.subject}>
          {message.subject}
        </p>
        <p className="truncate text-[12.5px] leading-5 text-muted-foreground">
          {preview(message.body)}
        </p>
      </td>
      <td className="w-[20%] max-w-0 py-2.5 pr-3 align-top">
        <span
          className="block truncate font-mono text-[11.5px] leading-5 text-muted-foreground"
          title={prospect.targetUrl ?? undefined}
        >
          {prospect.targetUrl ? pathOf(prospect.targetUrl) : ''}
        </span>
      </td>
      <td className="w-8 pr-3 text-right align-middle">
        <ChevronRightIcon
          className="inline size-3.5 text-muted-foreground/40 transition-colors group-hover:text-plum"
          aria-hidden
        />
      </td>
    </tr>
  )
}
